import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  IconChain,
  OnSelectChange,
  Select,
  IconTrash,
  PermissionEnum,
  responseErrorsResolver,
  ResponseError,
} from '@ui/src';
import { ChainTypes } from './types';

import cn from './info.module.css';

const Chain = ({
  chain,
  setChains,
  chains,
  casp,
  setAddChainIsVisible,
  activePermissions,
  setDeletePopupVisible,
  setSelectedChainId,
  chainCreationErrors,
  chainCreationErrorsList,
  setChainCreationErrors,
  chainsList,
}: ChainTypes) => {
  const [globalError, setGlobalError] = useState('');

  const {
    setError,
    formState: { errors },
    clearErrors,
  } = useForm();

  const [values, setValues] = useState<{
    id: string;
    caspId: string;
    chain: string;
  }>();

  const onChange = (e: OnSelectChange) => {
    const nextValues = { ...values };

    if (e.target.name === `caspId-${chain?.id}`) {
      nextValues.caspId = e.target.value as string;
    } else if (e.target.name === `chain-${chain?.id}`) {
      nextValues.chain = e.target.value as string;
    }

    setValues(nextValues as { id: string; caspId: string; chain: string });

    if (chains) {
      const nextChains = chains?.map((item) => {
        let result = { ...item };

        if (result.id === chain?.id) {
          result = nextValues as {
            id: string;
            caspId: string;
            chain: string;
          };
        }

        return result;
      });

      setChains(nextChains);
    }
  };

  const onDelete = () => {
    if (chain?.id?.includes('chain')) {
      const nextChains = chains?.filter((item) => item.id !== chain?.id);

      setGlobalError('');
      clearErrors(['caspId', 'chain']);

      const nextChainCreationErrorsList = chainCreationErrorsList?.filter(
        (item) => item.id !== chain?.id,
      );

      setChainCreationErrors(
        nextChainCreationErrorsList as {
          id: string;
          error: ResponseError;
        }[],
      );

      if (nextChains?.length === 0) {
        setAddChainIsVisible(false);
      }

      setChains(nextChains as { id: string; caspId: string; chain: string }[]);
    } else {
      setDeletePopupVisible(true);
      setSelectedChainId(chain?.id as string);
    }
  };

  useEffect(() => {
    if (chain) {
      setValues(chain);
    }
  }, [chain]);

  useEffect(() => {
    if (chain?.id.includes('chain')) {
      if (chainCreationErrors) {
        responseErrorsResolver({
          errors: chainCreationErrors.error,
          setError,
          setGlobalError,
        });
      } else {
        setGlobalError('');
        clearErrors(['caspId', 'chain']);
      }
    }
  }, [chainCreationErrors, chain]);

  return (
    <div className={cn.chain}>
      <div className={cn.chainContent}>
        <div className={cn.chainLeft}>
          <div className={cn.chainTitle}>CASP (Internal)</div>
          <Select
            name={`caspId-${chain?.id}`}
            value={values?.caspId}
            placeholder="Select CASP"
            list={casp}
            onChange={onChange}
            disabled={!chain?.id.includes('chain')}
            error={errors?.caspId?.message as string}
            fullWidth
          />
        </div>
        <div className={cn.chainIcon}>
          <IconChain />
        </div>
        <div className={cn.chainRight}>
          <div className={cn.chainTitle}>Chain (Provider String)</div>
          <div className={cn.chainRightContent}>
            <div className={cn.chainSelect}>
              <Select
                name={`chain-${chain?.id}`}
                value={values?.chain}
                placeholder="Select Chain"
                list={chainsList?.map((item) => ({
                  name: item
                    .split('_')
                    .map((name) => name.toLowerCase())
                    .join('-'),
                  value: item,
                }))}
                onChange={onChange}
                disabled={!chain?.id.includes('chain')}
                error={errors?.chain?.message as string}
                fullWidth
              />
            </div>
            {(chain?.id?.includes('chain') ||
              (!chain?.id?.includes('chain') &&
                activePermissions?.includes(
                  PermissionEnum.PSP_INTEGRATION_CASPS_CHAINS_DELETE_ACTION,
                ))) && (
              <button
                type="button"
                className={cn.deleteChainButton}
                onClick={onDelete}
              >
                <IconTrash width={16} height={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {globalError && <div className={cn.chainGlobalError}>{globalError}</div>}
    </div>
  );
};

export default Chain;
